import { 
  generateSalesClosingStrategy, 
  generateClosingScript, 
  analyzeClosingOpportunity,
  isOpenAIAvailable,
  isQuotaExceeded
} from './openai.js';
import { getChatCompletion } from './aiIntegrations/chatCompletion';

/**
 * AI Sales Funnel Service
 * Provides AI-powered insights and automation for sales funnel management
 */

/**
 * Analyzes funnel performance and generates optimization recommendations
 */
export async function analyzeFunnelPerformance(funnelData, leads) {
  try {
    if (!isOpenAIAvailable()) {
      return getFallbackFunnelAnalysis('API not configured', false);
    }
    if (isQuotaExceeded()) {
      return getFallbackFunnelAnalysis('API quota exceeded', true);
    }

    const response = await getChatCompletion(
      'OPEN_AI',
      'gpt-4o',
      [
        {
          role: 'system',
          content: 'You are an AI sales funnel optimization expert. Analyze funnel performance data and leads to provide actionable insights. Respond with valid JSON.'
        },
        {
          role: 'user',
          content: `Analyze this sales funnel performance:\n\nFunnel Data: ${JSON.stringify(funnelData)}\nLeads Sample: ${JSON.stringify(leads?.slice(0, 10) || [])}\n\nRespond with JSON: { overall_health: string, conversion_rate: {current: number, benchmark: number, improvement_potential: number}, bottlenecks: [{stage, issue, impact, solution}], optimization_opportunities: [{opportunity, expected_lift, effort_required, implementation_steps}], lead_insights: {high_value_segments: [], behavioral_patterns: [], conversion_predictors: []}, recommendations: [{category, recommendation, priority, timeline}] }`
        }
      ],
      { max_completion_tokens: 3000, reasoning_effort: 'high' }
    );

    const content = response?.choices?.[0]?.message?.content || '{}';
    try { return JSON.parse(content); } catch { return getFallbackFunnelAnalysis('Parse error', false); }
  } catch (error) {
    console.error('Error in AI funnel analysis:', error);
    return getFallbackFunnelAnalysis(error?.message, error?.message?.includes('quota'));
  }
}

/**
 * Generates AI-powered lead scoring and prioritization
 */
export async function scoreLeadsWithAI(leads, criteria = {}) {
  try {
    if (!isOpenAIAvailable()) return getFallbackLeadScoring(leads, 'API not configured');
    if (isQuotaExceeded()) return getFallbackLeadScoring(leads, 'API quota exceeded');

    const response = await getChatCompletion(
      'OPEN_AI',
      'gpt-4o',
      [
        {
          role: 'system',
          content: 'You are an expert AI lead scoring specialist. Analyze lead data to provide intelligent scoring. Respond with valid JSON.'
        },
        {
          role: 'user',
          content: `Score and prioritize these leads:\n\nLeads Data: ${JSON.stringify(leads?.slice(0, 20) || [])}\nScoring Criteria: ${JSON.stringify(criteria)}\n\nRespond with JSON: { scored_leads: [{lead_id, ai_score, conversion_probability, priority_level, score_factors: {demographic, behavioral, engagement, fit}, key_insights: [], recommended_actions: [{action, timing, channel}]}], scoring_summary: {high_priority_count, average_score, top_conversion_indicators: []} }`
        }
      ],
      { max_completion_tokens: 3000, reasoning_effort: 'high' }
    );

    const content = response?.choices?.[0]?.message?.content || '{}';
    try { return JSON.parse(content); } catch { return getFallbackLeadScoring(leads, 'Parse error'); }
  } catch (error) {
    console.error('Error in AI lead scoring:', error);
    return getFallbackLeadScoring(leads, error?.message);
  }
}

/**
 * Generates AI-powered funnel optimization strategies
 */
export async function generateFunnelOptimization(currentFunnel, performanceData) {
  try {
    if (!isOpenAIAvailable()) return getFallbackOptimization('API not configured', false);
    if (isQuotaExceeded()) return getFallbackOptimization('API quota exceeded', true);

    const response = await getChatCompletion(
      'OPEN_AI',
      'gpt-4o',
      [
        {
          role: 'system',
          content: 'You are a sales funnel optimization strategist. Generate comprehensive strategies to optimize funnel performance. Respond with valid JSON.'
        },
        {
          role: 'user',
          content: `Analyze and optimize this sales funnel:\n\nCurrent Funnel: ${JSON.stringify(currentFunnel)}\nPerformance Data: ${JSON.stringify(performanceData)}\n\nRespond with JSON: { optimization_strategy: {focus_areas: [], expected_improvement, implementation_timeline}, stage_optimizations: [{stage_name, current_conversion, target_conversion, optimization_tactics: [{tactic, description, implementation_effort, expected_impact}]}], automation_opportunities: [{process, automation_type, benefits: [], implementation_complexity}], testing_recommendations: [{test_hypothesis, test_method, success_metrics: [], duration}] }`
        }
      ],
      { max_completion_tokens: 3000, reasoning_effort: 'high' }
    );

    const content = response?.choices?.[0]?.message?.content || '{}';
    try { return JSON.parse(content); } catch { return getFallbackOptimization('Parse error', false); }
  } catch (error) {
    console.error('Error in funnel optimization:', error);
    return getFallbackOptimization(error?.message, error?.message?.includes('quota'));
  }
}

/**
 * Generates AI-powered next best actions for leads
 */
export async function getNextBestActions(lead, interactionHistory) {
  try {
    if (!isOpenAIAvailable()) return getFallbackNextActions('API not configured');
    if (isQuotaExceeded()) return getFallbackNextActions('API quota exceeded');

    const response = await getChatCompletion(
      'OPEN_AI',
      'gpt-4o',
      [
        {
          role: 'system',
          content: 'You are an AI sales assistant specializing in lead nurturing. Recommend the most effective next actions. Respond with valid JSON.'
        },
        {
          role: 'user',
          content: `Recommend next best actions for this lead:\n\nLead Information: ${JSON.stringify(lead)}\nInteraction History: ${JSON.stringify(interactionHistory?.slice(-10) || [])}\n\nRespond with JSON: { lead_status: {current_stage, engagement_level, conversion_readiness}, recommended_actions: [{action_type, description, channel, timing, priority, expected_outcome, personalization_tips: []}], risk_factors: [{risk, mitigation}], success_indicators: [] }`
        }
      ],
      { max_completion_tokens: 2000, reasoning_effort: 'medium' }
    );

    const content = response?.choices?.[0]?.message?.content || '{}';
    try { return JSON.parse(content); } catch { return getFallbackNextActions('Parse error'); }
  } catch (error) {
    console.error('Error in next best actions:', error);
    return getFallbackNextActions(error?.message);
  }
}

/**
 * Generates AI-powered conversation starters for leads
 */
export async function generateConversationStarters(lead, context = 'general') {
  try {
    if (!isOpenAIAvailable()) return getFallbackConversationStarters('API not configured');
    if (isQuotaExceeded()) return getFallbackConversationStarters('API quota exceeded');

    const response = await getChatCompletion(
      'OPEN_AI',
      'gpt-4o',
      [
        {
          role: 'system',
          content: 'You are an expert sales conversation coach. Generate personalized, engaging conversation starters. Respond with valid JSON.'
        },
        {
          role: 'user',
          content: `Create conversation starters for this lead:\n\nLead Information: ${JSON.stringify(lead)}\nContext/Objective: ${context}\n\nRespond with JSON: { conversation_starters: [{opener, context, follow_up_questions: [], personalization_elements: []}], talking_points: [{topic, key_messages: [], relevance}], conversation_objectives: [] }`
        }
      ],
      { max_completion_tokens: 2000, reasoning_effort: 'medium' }
    );

    const content = response?.choices?.[0]?.message?.content || '{}';
    try { return JSON.parse(content); } catch { return getFallbackConversationStarters('Parse error'); }
  } catch (error) {
    console.error('Error in conversation starters:', error);
    return getFallbackConversationStarters(error?.message);
  }
}

// Fallback functions for when AI is unavailable

function getFallbackFunnelAnalysis(errorMessage, isQuotaIssue) {
  return {
    overall_health: 'Average',
    conversion_rate: { current: 0.15, benchmark: 0.20, improvement_potential: 0.25 },
    bottlenecks: [{ stage: 'Lead Qualification', issue: isQuotaIssue ? 'AI analysis unavailable - API quota exceeded' : 'Manual analysis required', impact: 'Medium', solution: isQuotaIssue ? 'Upgrade API plan for detailed insights' : 'Review qualification criteria manually' }],
    optimization_opportunities: [{ opportunity: isQuotaIssue ? 'Restore AI insights functionality' : 'Implement manual funnel analysis', expected_lift: '15-25%', effort_required: isQuotaIssue ? 'Low' : 'Medium', implementation_steps: isQuotaIssue ? ['Upgrade OpenAI API billing plan', 'Implement usage monitoring'] : ['Review funnel metrics manually', 'Identify conversion bottlenecks', 'Implement A/B testing'] }],
    lead_insights: { high_value_segments: ['Enterprise clients', 'Repeat customers'], behavioral_patterns: ['Email engagement correlates with conversion'], conversion_predictors: ['Demo requests', 'Pricing page visits'] },
    recommendations: [{ category: isQuotaIssue ? 'API Management' : 'Analysis', recommendation: isQuotaIssue ? 'Upgrade OpenAI API plan to restore full AI analytics capabilities' : 'Implement manual funnel tracking and analysis processes', priority: 'High', timeline: isQuotaIssue ? 'Immediate' : 'This week' }]
  };
}

function getFallbackLeadScoring(leads, errorMessage) {
  const scoredLeads = (leads || [])?.map((lead, index) => ({
    lead_id: lead?.id?.toString() || index?.toString(),
    ai_score: Math.floor(Math.random() * 40) + 50,
    conversion_probability: Math.random() * 0.5 + 0.3,
    priority_level: ['High', 'Medium', 'Low']?.[Math.floor(Math.random() * 3)],
    score_factors: { demographic: Math.floor(Math.random() * 30) + 70, behavioral: Math.floor(Math.random() * 40) + 60, engagement: Math.floor(Math.random() * 35) + 65, fit: Math.floor(Math.random() * 25) + 75 },
    key_insights: [errorMessage?.includes('quota') ? 'AI scoring unavailable - upgrade API plan' : 'Manual scoring applied', 'Based on available data patterns'],
    recommended_actions: [{ action: errorMessage?.includes('quota') ? 'Upgrade API plan for AI insights' : 'Manual lead review', timing: 'Immediate', channel: 'Internal' }]
  }));
  return {
    scored_leads: scoredLeads,
    scoring_summary: { high_priority_count: scoredLeads?.filter(l => l?.priority_level === 'High')?.length, average_score: scoredLeads?.reduce((acc, l) => acc + l?.ai_score, 0) / (scoredLeads?.length || 1), top_conversion_indicators: ['Email engagement', 'Website activity', 'Demo requests'] }
  };
}

function getFallbackOptimization(errorMessage, isQuotaIssue) {
  return {
    optimization_strategy: { focus_areas: [isQuotaIssue ? 'API Configuration' : 'Manual Analysis', 'Lead Qualification', 'Follow-up Process'], expected_improvement: isQuotaIssue ? 'Restore AI capabilities' : '10-15% conversion improvement', implementation_timeline: '2-4 weeks' },
    stage_optimizations: [{ stage_name: 'Lead Capture', current_conversion: '25%', target_conversion: '30%', optimization_tactics: [{ tactic: isQuotaIssue ? 'Upgrade API plan' : 'Improve form design', description: isQuotaIssue ? 'Restore AI optimization capabilities' : 'Simplify lead capture forms', implementation_effort: 'Low', expected_impact: 'Medium' }] }],
    automation_opportunities: [{ process: isQuotaIssue ? 'AI Analytics' : 'Lead Scoring', automation_type: isQuotaIssue ? 'API Integration' : 'Manual Process', benefits: [isQuotaIssue ? 'Restore AI insights' : 'Consistent scoring', 'Time savings'], implementation_complexity: isQuotaIssue ? 'Simple' : 'Moderate' }],
    testing_recommendations: [{ test_hypothesis: isQuotaIssue ? 'AI insights improve conversion rates' : 'Improved follow-up timing increases conversions', test_method: isQuotaIssue ? 'A/B test with/without AI' : 'A/B test timing variations', success_metrics: ['Conversion rate', 'Response rate'], duration: '2 weeks' }]
  };
}

function getFallbackNextActions(errorMessage) {
  return {
    lead_status: { current_stage: 'Qualification', engagement_level: 'Medium', conversion_readiness: 'Warming' },
    recommended_actions: [{ action_type: errorMessage?.includes('quota') ? 'API Configuration' : 'Follow-up Call', description: errorMessage?.includes('quota') ? 'Upgrade OpenAI API plan to get AI-powered lead recommendations' : 'Schedule follow-up call to assess interest level', channel: errorMessage?.includes('quota') ? 'Internal' : 'Phone', timing: 'Immediate', priority: 'High', expected_outcome: errorMessage?.includes('quota') ? 'Restore AI recommendations' : 'Qualify lead interest and needs', personalization_tips: [errorMessage?.includes('quota') ? 'Upgrade plan for personalized insights' : 'Reference previous interactions', 'Focus on specific pain points'] }],
    risk_factors: [{ risk: errorMessage?.includes('quota') ? 'Limited AI insights' : 'Lead may lose interest', mitigation: errorMessage?.includes('quota') ? 'Upgrade API plan' : 'Maintain regular contact' }],
    success_indicators: ['Increased engagement', 'Response to outreach', 'Meeting requests']
  };
}

function getFallbackConversationStarters(errorMessage) {
  return {
    conversation_starters: [{ opener: errorMessage?.includes('quota') ? 'I wanted to reach out personally...' : 'I noticed you\'ve been exploring solutions for...', context: 'General outreach', follow_up_questions: ['What\'s your biggest challenge right now?', 'How are you currently handling this?', 'What would an ideal solution look like?'], personalization_elements: [errorMessage?.includes('quota') ? 'API quota exceeded - upgrade needed' : 'Company name', 'Industry reference', 'Recent activity'] }],
    talking_points: [{ topic: errorMessage?.includes('quota') ? 'API Configuration' : 'Value Proposition', key_messages: [errorMessage?.includes('quota') ? 'Upgrade API plan for personalized talking points' : 'How we help similar companies', 'Specific benefits for their industry', 'Success stories and case studies'], relevance: errorMessage?.includes('quota') ? 'API functionality' : 'High - industry-specific' }],
    conversation_objectives: [errorMessage?.includes('quota') ? 'Restore AI conversation assistance' : 'Identify pain points', 'Build rapport', 'Schedule next interaction']
  };
}

// Wrapper functions that use existing OpenAI functions
export {
  generateSalesClosingStrategy,
  generateClosingScript,
  analyzeClosingOpportunity
};